import './InviteModal.scss';
import { AsyncStateNotification } from '@ui/notifications';
import { Button } from 'antd';
import { createAsyncSelectors } from '@lib/selectors';
import { securityActions } from '@sonar-software/sonar-security';
import { SonarModal } from '@ui/modals';
import { UserWithMetadata } from '@sonar-software/sonar-core';
import { COPY_GENERIC, COPY_SPECIFIC, NOTIFICATIONS_COPY } from '@sonar-software/sonar-ts-constants';
import { CopyText, SonarText } from '@ui/text';
import React, { useCallback, useEffect } from 'react';
import { useAction, useSonarSelector } from '@hooks';

export interface InviteModalProps {
	afterClose: () => void;
	inviteLink: string;
	onCancel: () => void;
	user: UserWithMetadata;
	open: boolean;
}

const inviteUserByIdSelectors = createAsyncSelectors((state) => state.security.invitations.resend.post);

export const InviteModal: React.FC<InviteModalProps> = ({ afterClose, inviteLink, onCancel, user, open }) => {
	const isResendRequesting = useSonarSelector(inviteUserByIdSelectors.isRequested);
	const isResendSuccessful = useSonarSelector(inviteUserByIdSelectors.isReceived);

	const requestResendInvite = useAction(securityActions.requestPostSecurityInvitationsResend);

	const handleResendEmail = useCallback(() => {
		requestResendInvite({
			userId: user.id
		});
	}, [ requestResendInvite, user.id ]);

	useEffect(() => {
		if (isResendSuccessful) {
			onCancel();
		}
	}, [ isResendSuccessful, onCancel ]);

	return (
		<>
			<SonarModal
				afterClose={ afterClose }
				centered
				className='invite-modal'
				footer={
					<Button
						key='submit'
						loading={ isResendRequesting }
						onClick={ handleResendEmail }
						type='primary'>
						{ COPY_SPECIFIC.RESEND_EMAIL }
					</Button>
				}
				onCancel={ onCancel }
				open={ open }
				title={ COPY_SPECIFIC.USER_INVITATION_OPTIONS }>
				<SonarText className='sub-text'>{ COPY_GENERIC.INVITE_SUBTEXT(user.email) }</SonarText>
				<CopyText
					className='invite-link'
					text={ inviteLink } />
			</SonarModal>
			<AsyncStateNotification
				errored={ NOTIFICATIONS_COPY.ERROR_POST_USER_INVITE }
				failed={ NOTIFICATIONS_COPY.FAILED_POST_USER_INVITE }
				received={ NOTIFICATIONS_COPY.SUCCESS_POST_USER_INVITE }
				resetRawAction={ securityActions.resetPostSecurityInvitationsResend }
				stateSelector={ inviteUserByIdSelectors.state } />
		</>
	);
};
